import { useMemo, useState } from 'react'

import { useRun, useScenari, useScenariPerEnvironment } from './api'
import {
  commutaId,
  filtraCatalogo,
  leggiEnvironmentSalvati,
  pianoCampagna,
  raggruppaCatalogo,
  salvaEnvironment
} from './Functions'

/**
 * Lo stato del form di lancio e tutto ciò che ne deriva: catalogo filtrato e raggruppato,
 * piano per environment, scenari «falliti» dell'ultima run conclusa. Nessuna scelta di
 * scenari vale «tutti quelli visibili».
 * @param {Object} opzioni - Opzioni
 * @param {boolean} opzioni.aperto - Il form è aperto (solo allora si legge l'ultima run)
 * @param {string|null} opzioni.ultimaConclusaId - L'ultima run conclusa, per il preset «falliti»
 */
export const useModuloLancio = ({ aperto, ultimaConclusaId }) => {
  const [form, setForm] = useState(() => ({
    // Nell'ordine scelto: il primo è quello su cui girano i comuni.
    environmentIds: leggiEnvironmentSalvati(),
    scenari: [],
    ripetizioni: '',
    ricerca: 'finta',
    google: 'auto',
    nome: '',
    trascrizioni: false
  }))
  const [filtroCatalogo, setFiltroCatalogo] = useState({ testo: '', preset: 'tutti' })
  const scenariQuery = useScenari()
  const catalogo = scenariQuery.data
  const perEnvironment = useScenariPerEnvironment(form.environmentIds)
  const cataloghiPronti = perEnvironment.every((voce) => voce.catalogo)

  const { data: ultimaRun } = useRun(aperto ? ultimaConclusaId : null)
  const fallitiUltima = useMemo(
    () =>
      (ultimaRun?.risultati ?? [])
        .filter((r) => !r.saltato && r.passato === false)
        .map((r) => r.id),
    [ultimaRun]
  )
  const visibili = useMemo(
    () => filtraCatalogo(catalogo?.scenari ?? [], { ...filtroCatalogo, falliti: fallitiUltima }),
    [catalogo, filtroCatalogo, fallitiUltima]
  )
  const gruppi = useMemo(() => raggruppaCatalogo(visibili), [visibili])
  const sceltiOVisibili = form.scenari.length ? form.scenari : visibili.map((s) => s.id)
  const piano = useMemo(
    () => (cataloghiPronti ? pianoCampagna(sceltiOVisibili, perEnvironment, form.ripetizioni) : []),
    // `perEnvironment` cambia identità a ogni render: contano i cataloghi arrivati.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cataloghiPronti, sceltiOVisibili.join(','), form.environmentIds.join(','), form.ripetizioni]
  )
  const esecuzioniPiano = piano.reduce((somma, voce) => somma + voce.esecuzioni, 0)

  const aggiorna = (campo, valore) => setForm((prima) => ({ ...prima, [campo]: valore }))
  const commuta = (ids, acceso) =>
    setForm((prima) => ({
      ...prima,
      scenari: acceso
        ? [...new Set([...prima.scenari, ...ids])]
        : prima.scenari.filter((id) => !ids.includes(id))
    }))
  const commutaEnvironment = (id) =>
    setForm((prima) => {
      const environmentIds = commutaId(prima.environmentIds, id)
      salvaEnvironment(environmentIds)
      return { ...prima, environmentIds }
    })
  const selezionaVisibili = () =>
    aggiorna(
      'scenari',
      visibili.map((s) => s.id)
    )
  const deseleziona = () => aggiorna('scenari', [])
  const scegliPreset = (preset) => {
    setFiltroCatalogo((f) => ({ ...f, preset }))
    aggiorna('scenari', [])
  }

  return {
    form,
    aggiorna,
    commuta,
    commutaEnvironment,
    filtroCatalogo,
    setFiltroCatalogo,
    scegliPreset,
    selezionaVisibili,
    deseleziona,
    scenariQuery,
    catalogo,
    perEnvironment,
    cataloghiPronti,
    fallitiUltima,
    visibili,
    gruppi,
    piano,
    esecuzioniPiano
  }
}
